import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../hooks/useAuth';
import { crmApi, opportunityApi, watchListApi, userApi, subscriptionApi, pipelineApi, MatchRow } from '../../services/api';
import { formatAgency } from '../../utils/agency';

import {
  ClockIcon,
  CurrencyDollarIcon,
  SparklesIcon,
  LockClosedIcon,
  EyeIcon,
  CalendarDaysIcon,
  ArrowRightIcon,
  CrosshairIcon,
  GaugeIcon,
  CompassIcon,
  ScrollIcon,
  KanbanIcon,
} from 'components/icons';
import LearningFeedback from '../../components/prompts/LearningFeedback';
import PageHeader from '../../components/ui/PageHeader';
import EmptyState from '../../components/ui/EmptyState';
import ErrorState from '../../components/ui/ErrorState';
import { SkeletonBlock, SkeletonCards } from '../../components/ui/Skeleton';
/** Stages that are no longer in play; shared by the deadline and value figures. */
const CLOSED = ['awarded', 'lost', 'archived'];

const Dashboard: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const [metrics, setMetrics] = useState<any>(null);
  const [recommendations, setRecommendations] = useState<any[]>([]);
  const [matches, setMatches] = useState<MatchRow[]>([]);
  // null until the request answers; 0 means it answered with none.
  const [matchCount, setMatchCount] = useState<number | null>(null);
  const [watchedItems, setWatchedItems] = useState<any[]>([]);
  // Pursuits carry the obligations that are actually due (site visits,
  // question cut-offs, the response itself). The watch list only knows the
  // response date, and the report caught the two disagreeing.
  const [pursuitRows, setPursuitRows] = useState<any[]>([]);
  const [pursuitError, setPursuitError] = useState(false);
  const [inferredPrefs, setInferredPrefs] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showUpgradeSuccess, setShowUpgradeSuccess] = useState(false);
  const [activating, setActivating] = useState(false);
  const [activationError, setActivationError] = useState<string | null>(null);
  const [watchError, setWatchError] = useState(false);
  const [metricsError, setMetricsError] = useState(false);

  const isFreePlan = user?.plan === 'FREE';
  const isPro = user?.plan === 'PRO' || user?.plan === 'ENTERPRISE';

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const upgraded = urlParams.get('upgraded') === 'true';
    const sessionId = urlParams.get('session_id');

    if (upgraded && sessionId) {
      // Server-side reconciliation: hit Stripe directly so we don't depend
      // on the webhook arriving before the user clicks around.
      activateSubscription(sessionId);
      window.history.replaceState({}, '', '/dashboard');
    } else if (upgraded) {
      // Legacy redirect without session_id — fall back to refresh.
      setShowUpgradeSuccess(true);
      refreshUser();
      window.history.replaceState({}, '', '/dashboard');
    }

    fetchDashboardData();
  // Mount-once: reads the Stripe redirect params and loads the widgets. Listing the fetchers or refreshUser would re-run the activation flow on every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activateSubscription = async (sessionId: string) => {
    setActivating(true);
    setActivationError(null);

    // Stripe sometimes finalizes the session a beat after the redirect.
    // Retry on 409 (not-yet-complete) with short backoff.
    const maxAttempts = 5;
    const delayMs = [500, 1000, 2000, 3000, 5000];

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        await subscriptionApi.syncCheckoutSession(sessionId);
        await refreshUser();
        setActivating(false);
        setShowUpgradeSuccess(true);
        return;
      } catch (err: any) {
        const status = err.response?.status;
        const isLastAttempt = attempt === maxAttempts - 1;

        if (status === 409 && !isLastAttempt) {
          await new Promise((r) => setTimeout(r, delayMs[attempt]));
          continue;
        }

        console.error('Subscription activation failed:', err);
        setActivating(false);
        setActivationError(
          err.response?.data?.error ||
            'We could not confirm your upgrade. If you were charged, please refresh in a minute or contact support.',
        );
        // Best-effort refresh in case the webhook already won the race.
        refreshUser();
        return;
      }
    }
  };

  // Each widget owns its own failure so one dead endpoint doesn't blank the
  // page — and, more importantly, so a failure is visible at all. Previously
  // every request was `.catch(() => null)`, which rendered an empty widget
  // indistinguishable from "you have no watched opportunities".
  const fetchDashboardData = async () => {
    setLoading(true);
    setWatchError(false);
    setMetricsError(false);
    setPursuitError(false);

    const tasks: Promise<unknown>[] = [];

    tasks.push(
      pipelineApi
        .listPursuits()
        .then((r) => setPursuitRows(r.data?.data?.rows || []))
        .catch((error) => { console.error('Pursuits failed:', error); setPursuitError(true); }),
    );

    if (isPro) {
      tasks.push(
        Promise.all([
          crmApi.getDashboard(),
          opportunityApi.getRecommendations(),
        ])
          .then(([crmRes, recRes]) => {
            setMetrics(crmRes.data.metrics);
            setRecommendations(recRes.data.recommendations?.slice(0, 3) || []);
          })
          .catch((error) => {
            console.error('Dashboard metrics failed:', error);
            setMetricsError(true);
          }),
      );
      // What the matcher found since the last visit. Its own request, so a
      // failure here does not take the metrics down with it.
      tasks.push(
        opportunityApi
          .getMatches({ limit: 3 })
          .then((r) => { setMatches(r.data.matches); setMatchCount(r.data.newCount); })
          .catch((error) => { console.error('Matches failed:', error); setMatchCount(0); }),
      );
    }

    tasks.push(
      watchListApi
        .getAll({ limit: 5 })
        .then((watchRes) => {
          // The endpoint answers {success, data:{items,total,...}}. This used to
          // read `.data.items` — undefined — then fall back to `.data`, which is
          // the wrapper object rather than an array, so it always resolved to []
          // and both Recently Watched and Upcoming Deadlines were permanently
          // empty for everyone. WatchList.tsx reads the same endpoint correctly.
          const body = watchRes.data;
          const items = body?.data?.items ?? body?.items ?? body;
          setWatchedItems(Array.isArray(items) ? items : []);
        })
        .catch((error) => {
          console.error('Watch list failed:', error);
          setWatchError(true);
        }),
    );

    tasks.push(
      // Preferences are genuinely optional garnish — a failure here degrades
      // to the "start browsing" prompt, which is a reasonable resting state.
      userApi
        .getInferredPreferences()
        .then((prefsRes) => setInferredPrefs(prefsRes.data))
        .catch(() => {}),
    );

    await Promise.all(tasks);
    setLoading(false);
  };

  // No hardcoded trend deltas — a real value wrapped in an invented trend is
  // worse than no trend. Re-add `change` only when the API returns a real
  // period-over-period figure.
  // A zero is shown as "not yet", with the step that changes it, rather than
  // as "$0.0M" and "0%" — which read as broken metrics on a new account.
  const totalOpps = metrics?.pipeline?.totalOpportunities || 0;
  const totalValue = metrics?.pipeline?.totalValue || 0;
  const winRate = metrics?.winRate?.ytd || 0;
  const dueThisWeekFromPursuits = pursuitRows.filter((r: any) => {
    if (CLOSED.includes(r.stage) || !r.responseDeadline) return false;
    const t = new Date(r.responseDeadline).getTime();
    return t >= Date.now() && t - Date.now() <= 7 * 86400000;
  }).length;
  const dueThisWeek = pursuitRows.length > 0 ? dueThisWeekFromPursuits : (metrics?.upcoming?.dueThisWeek || 0);
  // Pipeline value as evidence: the sum of each active pursuit's most relevant
  // prior award. Falls back to the CRM figure (SAM-stated or awarded amounts).
  const activeRows = pursuitRows.filter((r: any) => !CLOSED.includes(r.stage));
  const valuedRows = activeRows.filter((r: any) => r.estimatedValue?.amount > 0);
  const priorAwardValue = valuedRows.reduce((s: number, r: any) => s + r.estimatedValue.amount, 0);
  const pipelineValue = priorAwardValue > 0 ? priorAwardValue : totalValue;
  const fmtMoney = (n: number) => n >= 1e6 ? `$${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `$${Math.round(n / 1e3)}K` : `$${Math.round(n)}`;
  const stats: Array<{ name: string; value: string; hint: string | null; icon: any; tone: string; href?: string }> = [
    { name: 'Active Pursuits', value: String(totalOpps), hint: totalOpps ? null : 'Nothing in your pipeline yet', icon: CrosshairIcon, tone: 'bg-sky-50 text-sky-600', href: '/pipeline?view=list' },
    { name: 'Pipeline Value', value: pipelineValue ? fmtMoney(pipelineValue) : '—', hint: priorAwardValue > 0 ? `Prior awards for ${valuedRows.length} of ${activeRows.length} pursuits` : pipelineValue ? null : (activeRows.length ? 'No comparable prior awards yet' : 'Add a pursuit to track value'), icon: CurrencyDollarIcon, tone: 'bg-emerald-50 text-emerald-600', href: '/pipeline?view=list' },
    { name: 'Win Rate', value: winRate ? `${winRate}%` : '—', hint: winRate ? null : 'No awards yet', icon: GaugeIcon, tone: 'bg-violet-50 text-violet-600' },
    { name: 'Due This Week', value: String(dueThisWeek), hint: dueThisWeek ? null : 'Nothing due this week', icon: ClockIcon, tone: 'bg-amber-50 text-amber-600', href: dueThisWeek ? '/pipeline?view=list&filter=week' : '/pipeline?view=list' },
  ];

  const quickActions = [
    { name: 'Find Opportunities', href: '/opportunities', icon: CompassIcon, tone: 'bg-violet-50 text-violet-600 ring-violet-600/15', free: true },
    { name: 'Ask AI Assistant', href: '/ai-assistant', icon: SparklesIcon, tone: 'bg-sky-50 text-sky-600 ring-sky-600/15', free: false },
    { name: 'Write Proposal', href: '/proposal-writer', icon: ScrollIcon, tone: 'bg-emerald-50 text-emerald-600 ring-emerald-600/15', free: false },
    { name: 'View Pursuits', href: '/pipeline', icon: KanbanIcon, tone: 'bg-amber-50 text-amber-600 ring-amber-600/15', free: true },
  ];

  // Format deadline as relative time
  const formatDeadline = (deadline: string) => {
    if (!deadline) return null;
    const date = new Date(deadline);
    const now = new Date();
    const diffDays = Math.ceil((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return { text: 'Expired', color: 'text-red-500' };
    if (diffDays === 0) return { text: 'Due today', color: 'text-red-600 font-semibold' };
    if (diffDays <= 3) return { text: `${diffDays}d left`, color: 'text-orange-600 font-semibold' };
    if (diffDays <= 7) return { text: `${diffDays}d left`, color: 'text-amber-600' };
    return { text: `${diffDays}d left`, color: 'text-content-muted' };
  };

  // Nearest obligation first, from the pursuits; the watch list fills in only
  // when nothing is in the pipeline yet.
  const pursuitDeadlines = pursuitRows
    .filter((r: any) => !CLOSED.includes(r.stage) && r.soonestAt && new Date(r.soonestAt).getTime() > Date.now())
    .sort((a: any, b: any) => new Date(a.soonestAt).getTime() - new Date(b.soonestAt).getTime())
    .slice(0, 5);
  const watchDeadlines = watchedItems
    .filter((item: any) => {
      const opp = item.opportunity || item;
      const deadline = opp.responseDeadline || opp.dueDate;
      return deadline && new Date(deadline) > new Date();
    })
    .sort((a: any, b: any) => {
      const aDate = new Date((a.opportunity || a).responseDeadline || (a.opportunity || a).dueDate);
      const bDate = new Date((b.opportunity || b).responseDeadline || (b.opportunity || b).dueDate);
      return aDate.getTime() - bDate.getTime();
    })
    .slice(0, 5);
  const deadlinesFromPursuits = pursuitRows.length > 0;
  const upcomingDeadlines = deadlinesFromPursuits ? pursuitDeadlines : watchDeadlines;

  // Build recommendation search link from inferred preferences
  const getRecommendedSearchLink = () => {
    if (!inferredPrefs || inferredPrefs.interactionCount < 3) return null;
    const params = new URLSearchParams();
    const topNaics = inferredPrefs.topNaicsCodes?.[0];
    const topAgency = inferredPrefs.topAgencies?.[0];
    if (topNaics?.code) params.set('naicsCode', topNaics.code);
    if (topAgency?.name) params.set('agency', topAgency.name);
    return `/opportunities?${params.toString()}`;
  };

  return (
    <div className="space-y-8">
      {/* Welcome Section */}
      <PageHeader
        title={`Welcome back, ${user?.firstName || 'there'}`}
        subtitle="Here's what's happening with your GovCon business today."
      />

      {/* Activating Subscription Overlay */}
      {activating && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-brand-50 border border-brand-200 rounded-lg p-6"
        >
          <div className="flex items-center gap-3">
            <div className="h-5 w-5 border-2 border-brand-600 border-t-transparent rounded-full animate-spin" />
            <div>
              <h2 className="text-lg font-semibold text-brand-900">Activating your subscription…</h2>
              <p className="text-sm text-brand-700">This usually takes a few seconds.</p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Activation Error */}
      {activationError && !activating && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-900"
        >
          <p className="text-sm">{activationError}</p>
        </motion.div>
      )}

      {/* Upgrade Success Banner */}
      {showUpgradeSuccess && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-green-500 rounded-lg p-6 text-white"
        >
          <div className="flex items-center gap-3">
            <SparklesIcon className="h-6 w-6" />
            <div>
              <h2 className="text-xl font-bold">Welcome to Pro!</h2>
              <p className="text-green-100">
                Your subscription is now active. You have access to all Pro features.
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Compact Upgrade Banner for Free Users */}
      {isFreePlan && (
        <div className="bg-brand-50 border border-brand-200 rounded-lg px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <SparklesIcon className="h-5 w-5 text-brand-600" />
            <span className="text-sm text-brand-700">
              Unlock AI proposals, advanced search, and more with Pro
            </span>
          </div>
          <Link
            to="/pricing"
            className="text-sm font-medium text-brand-600 hover:text-brand-500 whitespace-nowrap"
          >
            View Plans &rarr;
          </Link>
        </div>
      )}

      {/* Learning Feedback */}
      <LearningFeedback inferredPrefs={inferredPrefs} />

      {/* Stats Grid — Pro only */}
      {isPro && loading && <SkeletonCards count={4} />}

      {isPro && !loading && metricsError && (
        <div className="card">
          <ErrorState
            title="Pipeline metrics didn't load"
            description="Your opportunities are unaffected — this is just the summary above them."
            onRetry={fetchDashboardData}
            retrying={loading}
            compact
          />
        </div>
      )}

      {isPro && !loading && !metricsError && (
        <div className="grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-4">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              className={`bg-surface overflow-hidden shadow rounded-lg ${stat.href ? 'hover:shadow-md transition-shadow' : ''}`}
            >
              {(() => {
                const inner = (
                  <div className="flex items-center">
                    <div className={`flex-shrink-0 p-2 rounded-lg ${stat.tone}`}>
                      <stat.icon className="h-5 w-5" />
                    </div>
                    <div className="ml-3 sm:ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-xs sm:text-sm font-medium text-content-muted leading-tight">{stat.name}</dt>
                        <dd className="flex items-baseline">
                          <div className={`stat-value ${stat.value === '—' ? 'text-content-subtle' : 'text-content'}`}>{stat.value}</div>
                        </dd>
                        {stat.hint && <p className="text-caption text-content-subtle mt-0.5 leading-tight">{stat.hint}</p>}
                      </dl>
                    </div>
                  </div>
                );
                return stat.href
                  ? <Link to={stat.href} className="block p-4 sm:p-5 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent">{inner}</Link>
                  : <div className="p-4 sm:p-5">{inner}</div>;
              })()}
            </motion.div>
          ))}
        </div>
      )}

      {/* Quick Actions */}
      <div className="bg-surface shadow rounded-lg p-6">
        <h2 className="text-title text-content mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {quickActions.map((action) => {
            const isLocked = !action.free && isFreePlan;
            return (
              <Link
                key={action.name}
                to={isLocked ? '/pricing' : action.href}
                className={`flex flex-col items-center p-4 rounded-lg transition-colors relative ${
                  isLocked ? 'bg-surface-sunk hover:bg-gray-200' : 'hover:bg-surface-sunk'
                }`}
              >
                {isLocked && (
                  <div className="absolute top-2 right-2">
                    <LockClosedIcon className="h-4 w-4 text-content-subtle" />
                  </div>
                )}
                <div className={`p-3 rounded-xl ring-1 ring-inset ${action.tone} ${isLocked ? 'opacity-50' : ''}`}>
                  <action.icon className="h-7 w-7" />
                </div>
                <span className={`mt-2 text-sm font-medium ${isLocked ? 'text-content-muted' : 'text-content'}`}>
                  {action.name}
                </span>
                {isLocked && (
                  <span className="text-xs text-brand-600 mt-1">Upgrade</span>
                )}
              </Link>
            );
          })}
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Column: what the matcher found (Pro) / Recently Watched (Free) */}
        {isPro ? (
          <div className="bg-surface shadow rounded-lg">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <h2 className="text-title text-content flex items-center gap-2">
                  <CompassIcon className="h-5 w-5 text-accent" />
                  New matches
                  {!!matchCount && (
                    <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-2 rounded-full bg-accent text-white text-xs font-semibold">{matchCount > 99 ? '99+' : matchCount}</span>
                  )}
                </h2>
                <Link to="/opportunities?view=matches" className="text-sm text-brand-600 hover:text-brand-500">
                  {matchCount ? 'See all' : 'What I\'m watching'}
                </Link>
              </div>
            </div>
            <div className="p-6">
              {loading ? (
                <div className="space-y-3">
                  <span className="sr-only" role="status">Loading matches</span>
                  <SkeletonBlock className="h-14" />
                  <SkeletonBlock className="h-14" />
                  <SkeletonBlock className="h-14" />
                </div>
              ) : matches.length > 0 ? (
                <div className="space-y-2">
                  <p className="text-caption text-content-muted mb-3">New on SAM.gov and fitting everything you watch for.</p>
                  {matches.map((m) => {
                    const deadlineInfo = m.responseDeadline ? formatDeadline(m.responseDeadline) : null;
                    return (
                      <Link key={m.alertId} to={`/opportunities/${m.opportunityId}`} className="flex items-start justify-between gap-3 p-3 rounded-lg hover:bg-surface-sunk transition">
                        <div className="min-w-0 flex-1">
                          <h3 className="text-body font-semibold text-content line-clamp-2">{m.title}</h3>
                          <p className="text-caption text-content-muted truncate">
                            {[formatAgency(m.agencyPath), m.state, m.setAside && m.setAside !== 'NONE' ? `Set-aside: ${m.setAside}` : null].filter(Boolean).join(' · ')}
                          </p>
                          {m.reasons.length > 0 && (
                            <p className="text-caption text-content-subtle truncate mt-0.5">{m.reasons.slice(0, 2).join(' · ')}</p>
                          )}
                        </div>
                        {deadlineInfo && (
                          <span className={`text-caption whitespace-nowrap ${deadlineInfo.color}`}>{deadlineInfo.text}</span>
                        )}
                      </Link>
                    );
                  })}
                </div>
              ) : recommendations.length > 0 ? (
                <div>
                  <p className="text-caption text-content-muted mb-3">
                    Nothing new fits what you watch for yet. From your NAICS codes, worth a look:
                  </p>
                  <div className="space-y-2">
                    {recommendations.map((opp) => (
                      <Link key={opp.id} to={`/opportunities/${opp.id}`} className="block p-3 rounded-lg hover:bg-surface-sunk transition">
                        <h3 className="text-body font-semibold text-content line-clamp-2">{opp.title}</h3>
                        <p className="text-caption text-content-muted truncate">{formatAgency(opp.agency)}</p>
                        {opp.reason && <p className="text-caption text-content-subtle truncate mt-0.5">{opp.reason}</p>}
                      </Link>
                    ))}
                  </div>
                  <Link to="/opportunities" className="mt-4 inline-flex items-center text-sm text-brand-600 hover:text-brand-500 font-medium">
                    Choose what to watch for <ArrowRightIcon className="ml-1 h-4 w-4" />
                  </Link>
                </div>
              ) : (
                <EmptyState
                  icon={CompassIcon}
                  title="Nothing is being watched yet"
                  description="Set filters on the Opportunities page — NAICS, states, agencies, set-asides — and save them as what to watch. Every new posting that fits lands here and in a morning email."
                  actionLabel="Choose what to watch"
                  actionHref="/opportunities"
                  compact
                />
              )}
            </div>
          </div>
        ) : (
          /* Recently Watched - Available to ALL users */
          <div className="bg-surface shadow rounded-lg">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <h2 className="text-title text-content flex items-center gap-2">
                  <EyeIcon className="h-5 w-5 text-content-subtle" />
                  Recently Watched
                </h2>
                <Link to="/watchlist" className="text-sm text-brand-600 hover:text-brand-500">
                  View all
                </Link>
              </div>
            </div>
            <div className="p-6">
              {loading ? (
                <div className="space-y-3">
                  <span className="sr-only" role="status">Loading watched opportunities</span>
                  <SkeletonBlock className="h-12" />
                  <SkeletonBlock className="h-12" />
                  <SkeletonBlock className="h-12" />
                </div>
              ) : watchError ? (
                <ErrorState
                  title="Couldn't load your watch list"
                  onRetry={fetchDashboardData}
                  compact
                />
              ) : watchedItems.length > 0 ? (
                <div className="space-y-3">
                  {watchedItems.slice(0, 5).map((item: any) => {
                    const opp = item.opportunity || item;
                    const deadline = opp.responseDeadline || opp.dueDate;
                    const deadlineInfo = deadline ? formatDeadline(deadline) : null;
                    return (
                      <Link
                        key={item.id || opp.id}
                        to={`/opportunities/${opp.id || item.opportunityId}`}
                        className="flex items-center justify-between p-3 rounded-lg hover:bg-surface-sunk transition"
                      >
                        <div className="min-w-0 flex-1">
                          <h3 className="text-body font-semibold text-content truncate">{opp.title}</h3>
                          <p className="text-caption text-content-muted truncate">{formatAgency(opp.agency)}</p>
                        </div>
                        {deadlineInfo && (
                          <span className={`text-caption whitespace-nowrap ml-3 ${deadlineInfo.color}`}>
                            {deadlineInfo.text}
                          </span>
                        )}
                      </Link>
                    );
                  })}
                </div>
              ) : (
                <EmptyState
                  icon={EyeIcon}
                  title="No watched opportunities yet"
                  description="Star an opportunity to track its deadline and amendments here."
                  actionLabel="Browse opportunities"
                  actionHref="/opportunities"
                  compact
                />
              )}
            </div>
          </div>
        )}

        {/* Right Column: Upcoming Deadlines / Based on Activity */}
        <div className="space-y-8">
          {/* Upcoming Deadlines */}
          <div className="bg-surface shadow rounded-lg">
            <div className="p-6 border-b border-border">
              <h2 className="text-title text-content flex items-center gap-2">
                <CalendarDaysIcon className="h-5 w-5 text-content-subtle" />
                Upcoming Deadlines
              </h2>
            </div>
            <div className="p-6">
              {/* Deadlines are derived from the same watch-list fetch, so they
                  share its loading and error state. */}
              {loading ? (
                <div className="space-y-3">
                  <span className="sr-only" role="status">Loading deadlines</span>
                  <SkeletonBlock className="h-12" />
                  <SkeletonBlock className="h-12" />
                </div>
              ) : (deadlinesFromPursuits ? pursuitError : watchError) ? (
                <ErrorState
                  title="Couldn't load deadlines"
                  description={deadlinesFromPursuits ? 'Deadlines come from your pursuits, which did not load.' : 'Deadlines come from your watch list, which did not load.'}
                  onRetry={fetchDashboardData}
                  compact
                />
              ) : deadlinesFromPursuits && upcomingDeadlines.length > 0 ? (
                <div className="space-y-3">
                  {upcomingDeadlines.map((r: any) => {
                    const isResponse = !r.nextDue || !r.nextDue.deadline || new Date(r.nextDue.deadline).getTime() >= new Date(r.soonestAt).getTime();
                    const info = formatDeadline(r.soonestAt);
                    const KIND: Record<string, string> = { site_visit: 'Site visit', conference: 'Conference', rsvp: 'RSVP', base_access: 'Base access', question_cutoff: 'Questions due', registration: 'Registration' };
                    const what = isResponse ? 'Response due' : (KIND[r.nextDue.kind] || 'Deadline');
                    return (
                      <Link key={r.id} to={`/pursuits/${r.id}`} className="flex items-center justify-between p-3 rounded-lg hover:bg-surface-sunk transition">
                        <div className="min-w-0 flex-1">
                          <h3 className="text-body font-semibold text-content truncate">{r.title}</h3>
                          <p className="text-caption text-content-muted truncate">{what} · {r.nextStep?.title || formatAgency(r.agency)}</p>
                        </div>
                        {info && <span className={`text-caption whitespace-nowrap ml-3 ${info.color}`}>{info.text}</span>}
                      </Link>
                    );
                  })}
                  <Link to="/pipeline?view=list" className="block pt-1 text-label font-medium text-accent hover:text-accent-hover">All pursuits</Link>
                </div>
              ) : upcomingDeadlines.length > 0 ? (
                <div className="space-y-3">
                  {upcomingDeadlines.map((item: any) => {
                    const opp = item.opportunity || item;
                    const deadline = opp.responseDeadline || opp.dueDate;
                    const deadlineInfo = formatDeadline(deadline);
                    return (
                      <Link
                        key={item.id || opp.id}
                        to={`/opportunities/${opp.id || item.opportunityId}`}
                        className="flex items-center justify-between p-3 rounded-lg hover:bg-surface-sunk transition"
                      >
                        <div className="min-w-0 flex-1">
                          <h3 className="text-body font-semibold text-content truncate">{opp.title}</h3>
                          <p className="text-caption text-content-muted truncate">{formatAgency(opp.agency)}</p>
                        </div>
                        {deadlineInfo && (
                          <span className={`text-caption whitespace-nowrap ml-3 ${deadlineInfo.color}`}>
                            {deadlineInfo.text}
                          </span>
                        )}
                      </Link>
                    );
                  })}
                </div>
              ) : (
                <EmptyState
                  icon={CalendarDaysIcon}
                  title="No upcoming deadlines"
                  description={deadlinesFromPursuits ? 'Nothing in your pursuits is due. Deadlines appear here as you add solicitations to the pipeline.' : 'Add a solicitation to the pipeline to track its deadlines here.'}
                  compact
                />
              )}
            </div>
          </div>

          {/* Based on Your Activity */}
          {inferredPrefs && inferredPrefs.interactionCount >= 3 ? (
            <div className="bg-surface shadow rounded-lg">
              <div className="p-6 border-b border-border">
                <h2 className="text-title text-content flex items-center gap-2">
                  <SparklesIcon className="h-5 w-5 text-brand-500" />
                  Based on Your Activity
                </h2>
              </div>
              <div className="p-6">
                <p className="text-sm text-content-muted mb-4">
                  Clevor is learning your preferences. Here's what we've noticed:
                </p>
                <div className="space-y-2">
                  {inferredPrefs.topAgencies?.slice(0, 2).map((a: any) => (
                    <div key={a.name} className="flex items-center gap-2 text-sm">
                      <span className="w-2 h-2 bg-brand-500 rounded-full" />
                      <span className="text-content">You're interested in <strong>{a.name}</strong> contracts</span>
                    </div>
                  ))}
                  {inferredPrefs.topNaicsCodes?.slice(0, 1).map((n: any) => (
                    <div key={n.code} className="flex items-center gap-2 text-sm">
                      <span className="w-2 h-2 bg-purple-500 rounded-full" />
                      <span className="text-content">NAICS <strong>{n.code}</strong> appears frequently</span>
                    </div>
                  ))}
                </div>
                {getRecommendedSearchLink() && (
                  <Link
                    to={getRecommendedSearchLink()!}
                    className="mt-4 inline-flex items-center text-sm text-brand-600 hover:text-brand-500 font-medium"
                  >
                    See matching opportunities <ArrowRightIcon className="ml-1 h-4 w-4" />
                  </Link>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-surface-sunk border border-border rounded-lg p-6">
              <div className="flex items-center gap-3 mb-2">
                <SparklesIcon className="h-5 w-5 text-content-subtle" />
                <h3 className="text-sm font-medium text-content">Personalized Recommendations</h3>
              </div>
              <p className="text-sm text-content-muted">
                Browse and interact with opportunities to get personalized recommendations based on your interests.
              </p>
              <Link
                to="/opportunities"
                className="mt-3 inline-flex items-center text-sm text-brand-600 hover:text-brand-500"
              >
                Start browsing <ArrowRightIcon className="ml-1 h-4 w-4" />
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Consultant — a quiet prompt, not a billboard; Pro only */}
      {isPro && (
        <div className="bg-surface shadow rounded-lg p-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-3 min-w-0">
              <div className="flex-shrink-0 p-2 rounded-lg bg-accent-sunk text-accent">
                <SparklesIcon className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <h2 className="text-title text-content">Ask the consultant</h2>
                <p className="text-sm text-content-muted mt-0.5">Answers grounded in the solicitation's own documents and your company profile.</p>
              </div>
            </div>
            <Link to="/ai-assistant" className="btn-primary whitespace-nowrap self-start md:self-auto">Open chat</Link>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {[
              'What does this solicitation require of a small business?',
              'Which of my past projects fit this scope?',
              'What changed in the latest amendment?',
            ].map((question) => (
              <Link key={question} to="/ai-assistant" className="px-3 py-1.5 rounded-full bg-surface-sunk text-sm text-content hover:bg-accent-sunk hover:text-accent transition-colors">
                {question}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
